<template>
    <div
        class="rich-input input"
        contenteditable="true"
        ref="textareaInput"
        :id="contentId"
        :placeholder="placeholderTxt"
        @click="clickHandler"
        @focus="focusHandler"
        @blur="blurHandler"
        @keydown.delete="deleteKeyHandler"
        @mousemove="mousemoveHandler"
        @mouseout="mouseoutHandler"
        @mouseup="mouseupHandler"
        @paste="parseHandler"
        @compositionstart="compositionStart"
        @compositionend="compositionEnd"
        @input="inputHandler"
    ></div>
</template>

<script>
import { uniqueId } from "@/views/zhiji/js/common/util.js";

export default {
    name: "RichInput",
    props: ["value", "placeholder", "maxlength"],
    data() {
        return {
            contentId: `devinput${uniqueId()}`,
            isOnComposition: false,
            savedRange: "",
            oldText: ""
        };
    },
    computed: {
        placeholderTxt() {
            return this.placeholder ? this.placeholder : this.$t("zhiji.answer.textWidget.enterTextTip");
        }
    },
    watch: {
        value(val) {
            if (!this.isLocked) {
                this.$refs.textareaInput.innerHTML = `${val}<br>`;
                this.oldText = this.$refs.textareaInput.innerHTML;
            }
        }
    },
    created() {
        document.addEventListener("click", this.windowClick);
    },
    mounted() {
        this.$refs.textareaInput.innerHTML = `${this.value}<br>`;
        this.oldText = this.$refs.textareaInput.innerHTML;
        this.$emit("wordcount", this.$refs.textareaInput.textContent.length);
    },
    destroyed() {
        document.removeEventListener("click", this.windowClick);
    },
    methods: {
        windowClick(event) {
            if (!this.$el.contains(event.target)) {
                this.saveCurrentRange();
            }
        },
        compositionStart() {
            this.isOnComposition = true;
        },
        compositionEnd(event) {
            this.isOnComposition = false;
            this.inputHandler(event);
        },
        inputHandler() {
            if (this.isOnComposition) {
                return;
            }
            this.saveCurrentRange();
            this.calcWordCount();
            this.$emit("change", this.$refs.textareaInput);
        },
        // 计算字数
        calcWordCount() {
            const contentStr = this.$refs.textareaInput.textContent;
            const lengthStr = contentStr.length;
            if (lengthStr > this.maxlength) {
                this.$refs.textareaInput.innerHTML = this.oldText;
                if (this.wordCount < this.maxlength) {
                    const nowStr = this.$refs.textareaInput.innerHTML;
                    const lastEl = nowStr.substring(nowStr.length - 6, nowStr.length);
                    const subStr = contentStr.substring(this.wordCount, this.maxlength);
                    if (lastEl === "</div>") {
                        this.$refs.textareaInput.innerHTML = nowStr.slice(0, nowStr.length - 10) + subStr;
                    } else {
                        this.$refs.textareaInput.innerHTML += subStr;
                    }
                }
                this.wordCount = this.maxlength;
                this.setCursorToEnd();
            } else {
                this.wordCount = contentStr.length;
            }
            this.oldText = this.$refs.textareaInput.innerHTML;
            this.$emit("wordcount", this.wordCount);
        },
        deleteKeyHandler() {
            // TODO
        },
        // 鼠标移动到span等子元素上时，触发事件给上层处理
        mousemoveHandler(event) {
            const TAG_NAME = event.target.nodeName;
            if (TAG_NAME.toUpperCase() === "DIV") {
                return;
            }
            const id = event.target.id;
            if (!id) {
                return;
            }
            this.$emit("mouseAt", {
                id,
                position: this.getPosition(event.target)
            });
        },
        getPosition(target) {
            const rect = target.getBoundingClientRect();
            return rect;
        },
        mouseoutHandler() {
            this.$emit("mouseAt", {});
        },
        mouseupHandler() {
            this.saveCurrentRange();
        },
        // 粘贴，去除样式
        parseHandler(event) {
            event.preventDefault();
            const text = event.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text.replace(/\r\n/g, "\n"));
        },
        focusHandler() {
            this.isLocked = true;
        },
        blurHandler() {
            this.isLocked = false;
        },
        // 点击事件，向层次传递点中节点信息
        clickHandler(event) {
            const TAG_NAME = event.target.nodeName;

            // 编辑器根节点不需要处理
            if (TAG_NAME.toUpperCase() === "DIV") {
                return;
            }

            // 没有id的节点，没有数据，不需要进行处理
            const id = event.target.id;
            if (!id) {
                return;
            }
            this.$emit("clickAt", {
                id,
                position: this.getPosition(event.target)
            });
        },
        // 获取选中区域
        getRange() {
            if (window.getSelection) {
                let sel = window.getSelection();
                if (sel.rangeCount > 0) {
                    return sel.getRangeAt(0);
                }
            } else if (document.selection) {
                // IE兼容
                return document.selection.createRange();
            }
            return null;
        },
        saveCurrentRange() {
            // 保存当前光标与选区
            const range = this.getRange();
            if (range && range.commonAncestorContainer.ownerDocument.activeElement.id === this.contentId) {
                this.savedRange = range;
                const parent = range.commonAncestorContainer.parentNode;
                const id = parent.nodeName !== "DIV" ? parent.id : "";
                this.$emit("range", { text: range.toString(), id });
            }
            // 触发光标事件
            this.emitCursor();
        },
        // 触发光标事件，用于根据光标前的字符判断是否需要做特殊处理
        emitCursor() {
            if (this.savedRange && this.savedRange.commonAncestorContainer) {
                const range = this.savedRange;
                const content = range.commonAncestorContainer.textContent || "";
                const parent = range.commonAncestorContainer.parentNode;
                const id = parent.nodeName !== "DIV" ? parent.id : "";
                const rect = range.getBoundingClientRect();
                // 光标前一个字符
                const charBeforeCusor = content[range.startOffset - 1];
                if (range.collapsed) {
                    this.$emit("cursor", { char: charBeforeCusor, content, position: rect, id });
                    return;
                }
            }

            this.$emit("cursor", { char: "" });
        },
        // 直接替换元素
        replaceElementById(id, elemConfigs) {
            const target = document.getElementById(id);
            if (!target) {
                return;
            }
            elemConfigs.forEach(elemConfig => {
                const elem = this.createElementByTag(elemConfig);
                target.parentNode.insertBefore(elem, target);
            });
            target.parentElement.removeChild(target);
            this.$emit("change", this.$refs.textareaInput);
            this.calcWordCount();
        },
        // 在选中区域插入节点
        insertAtSeletionRange(elemConfig, toEnd) {
            const elem = this.createElementByTag(elemConfig);
            this.insertNodeAtSavedRange(elem);
            toEnd && this.setCursorToEnd();
        },
        createElementByTag({ id, tag, isText, innerText, attrs, styles, editable }) {
            if (isText) {
                return document.createTextNode(innerText);
            }
            const elem = document.createElement(tag);
            // 设置id
            id && (elem.id = id);
            // 设置文本、属性、样式
            this.setElementAttr(elem, { innerText, attrs, styles });
            // 设置内容是否可修改
            if (editable != null) {
                elem.setAttribute("contenteditable", editable);
            }
            return elem;
        },
        // 根据id更新元素属性
        updateNode({ id, innerText, attrs, styles }) {
            const elem = document.getElementById(id);
            if (!elem) {
                return;
            }
            this.setElementAttr(elem, { innerText, attrs, styles });

            this.$emit("change", this.$refs.textareaInput);
        },
        // 设置元素属性
        setElementAttr(elem, { innerText, attrs, styles }) {
            // 设置内容文本
            innerText && (elem.innerText = innerText);
            // 设置样式
            (styles || []).forEach(({ name, value }) => (elem.style[name] = value));
            // 设置属性
            (attrs || []).forEach(({ name, value }) => elem.setAttribute(name, value));
        },
        // 将光标移至文本最后
        setCursorToEnd() {
            const devInput = this.$refs.textareaInput;
            if (window.getSelection) {
                devInput.focus();
                const range = window.getSelection();
                range.selectAllChildren(devInput);
                range.collapseToEnd();
            } else {
                const range = document.selection.createRange();
                range.moveToElementText(devInput);
                range.collapse(false);
                range.select();
            }
        },
        // 插入元素节点到选中区域
        insertNodeAtSavedRange(node) {
            if (!this.savedRange.commonAncestorContainer) {
                return;
            }
            this.savedRange.deleteContents();
            this.savedRange.insertNode(node);

            this.$emit("change", this.$refs.textareaInput);
        },
        // 用元素文本内容替换节点
        replaceNodeWithContent({ id }) {
            const elem = document.getElementById(id);
            if (!elem) {
                return;
            }
            elem.replaceWith(elem.innerText);

            this.$emit("change", this.$refs.textareaInput);
        }
    }
};
</script>

<style lang="less">
.rich-input {
    display: inline-block;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    font-size: 12px;
    line-height: 21px;
    word-wrap: normal;
    word-break: normal;
    overflow-x: hidden;
    background: #fafbfd;
    outline: none;
    user-select: text;
    font: 400 13.3333px Arial;

    &:empty:before {
        content: attr(placeholder);
        color: #a0a0a0;
        font-size: 12px;
    }
    &:focus {
        outline: none !important;
    }

    p {
        display: inline !important;
    }
    i,
    span {
        cursor: pointer;
        font-style: normal;
        user-select: none;
        -webkit-user-select: none;
    }
    span[link-color="innerColor"]::before,
    span[link-color="outColor"]::before {
        content: "【";
    }
    span[link-color="innerColor"]::after,
    span[link-color="outColor"]::after {
        content: "】";
    }
}
</style>
